import moment from 'moment';
import { ConfigConstants } from '../config/config-constants';

export class DateUtilities {
    private static defaultBrowserLocale = 'en-US';
    private static browserLocale = DateUtilities.defaultBrowserLocale;
    /**
     * Convert a duration to a date object for use in HTML.
     * The duration is converted to a time on today's date which can be displayed by the native controls in iOS.
     *
     * @param {moment.Duration} duration
     * @returns {Date}
     */
    public static durationToDate(duration: moment.Duration): Date {
        const now = moment().startOf('day');
        now.add(duration.asMilliseconds(), 'milliseconds');

        return now.toDate();
    }

    /**
     * Sets the date portion of a moment object to the date given in a HTML5 formatted date string
     *
     * @param target
     * @param date
     * @param isMonthYearFormat
     */
    public static setDateHtml5(target: moment.Moment, date: string, isMonthYearFormat = false): moment.Moment {
        const parsedDate = isMonthYearFormat
            ? moment(date, moment.HTML5_FMT.MONTH, true)
            : moment(date, moment.HTML5_FMT.DATE, true);

        return DateUtilities.setDateMoment(target, parsedDate);
    }

    /**
     * Sets the date portion of a moment object based on a source moment object
     *
     * @param target
     * @param date
     */
    public static setDateMoment(target: moment.Moment, date: moment.Moment): moment.Moment {
        const dateObject = date.toObject();
        if (!target.isValid()) {
            target = moment();
        }

        target.set({
            years: dateObject.years,
            months: dateObject.months,
            date: dateObject.date,
        });
        return target;
    }

    public static setLocale(locale: string): void {
        DateUtilities.browserLocale = locale;
    }
    /**
     * Methods to format incoming moment date based on
     * browser's language locale settings
     *
     * @param date
     */
    public static formatDate(date: moment.Moment): string {
        if (!date || !date.isValid()) {
            return '';
        }

        const dateFormat = moment.localeData(DateUtilities.browserLocale)?.longDateFormat('L'); // i.e.: DD/MM/YYYY for en-GB or es-ES
        return dateFormat
            ? date.clone().local().locale(DateUtilities.browserLocale).format(dateFormat)
            : date.toISOString().split('T')[0]; // 2025-03-19
    }

    public static formatTime(date: moment.Moment): string {
        if (!date || !date.isValid()) {
            return '';
        }

        const timeFormat = moment.localeData(DateUtilities.browserLocale)?.longDateFormat('LT');
        return timeFormat
            ? date.clone().local().locale(DateUtilities.browserLocale).format(timeFormat)
            : date.format(ConfigConstants.formatPatternTimeGeneric);
    }

    public static formatWeekday(date: moment.Moment): string {
        if (!date || !date.isValid()) {
            return '';
        }

        const isUSLocale = DateUtilities.browserLocale.startsWith(DateUtilities.defaultBrowserLocale);
        const weekdayFormat = isUSLocale ? ConfigConstants.formatDayUs : ConfigConstants.formatDay;

        return date.locale(DateUtilities.browserLocale).format(weekdayFormat);
    }
}
